import { UserInterface } from "@common/models/user";
import type { AuthFailureKind } from "@common/bt-auth/auth-failure";
import {
  createSlice,
  PayloadAction
} from "@reduxjs/toolkit";

export interface SignupFormState {
  email?: string;
  password?: string;
  acceptedTerms?: boolean;
}

export type AuthenticationStatus =
  | 'idle'
  | 'autoJwtStarted'
  | 'autoJwtSuccess'
  | 'autoJwtFail'
  | 'userLoginStarted'
  | 'userLoginSuccess'
  | 'userRegisterStarted'
  | 'userRegisterSuccess'
  | 'userGoogleConnectStarted'
  | 'userGoogleConnectSuccess';

// Status groupings — single source of truth, consumed by route guards and the popup so
// the "busy / success / fail" classification can't drift between surfaces.
const BUSY_AUTH_STATUSES: readonly AuthenticationStatus[] = [
  'idle', // not started yet — treat as busy on protected surfaces until a status arrives
  'autoJwtStarted',
  'userLoginStarted',
  'userRegisterStarted',
  'userGoogleConnectStarted',
];
const SUCCESS_AUTH_STATUSES: readonly AuthenticationStatus[] = [
  'autoJwtSuccess',
  'userLoginSuccess',
  'userRegisterSuccess',
  'userGoogleConnectSuccess',
];

/** Auth is in-flight (or not yet started). Treated as "busy" by route guards / popup. */
export const isBusyAuthStatus = (status?: AuthenticationStatus): boolean =>
  BUSY_AUTH_STATUSES.includes(status as AuthenticationStatus);
/** Auth completed successfully (any strategy). */
export const isSuccessAuthStatus = (status?: AuthenticationStatus): boolean =>
  SUCCESS_AUTH_STATUSES.includes(status as AuthenticationStatus);
/** Auth ran and definitively failed. Only the auto-JWT path has a terminal failure status. */
export const isFailAuthStatus = (status?: AuthenticationStatus): boolean =>
  status === 'autoJwtFail';

export interface UserState {
  authenticationStatus: AuthenticationStatus;
  /**
   * WHY the last auth attempt failed. 'autoJwtFail' is a single bucket holding four very
   * different situations (no token / rejected token / unreachable backend / logout); route
   * guards must not treat a transient backend stall as a dead session. Only meaningful while
   * `authenticationStatus === 'autoJwtFail'`; always written ATOMICALLY with that status via
   * `setAuthFailure`, and cleared by any `setAuthenticationStatus`.
   */
  authFailureKind?: AuthFailureKind;
  user?: UserInterface;
  accessToken?: string | null;
  userId?: string;
  authError?: string;
  socketId?: string;
  socketModalOpen: boolean;
  hideSocketModal: boolean;
  signup: SignupFormState;
  direction: string;
  userWhatsappDirection: string;
  whatsappDarkMode: boolean; // WhatsApp's dark mode state (from body.dark class)
}

const initialState: UserState = {
  authenticationStatus: 'idle',
  signup: { email: "", password: "" },
  socketModalOpen: false,
  hideSocketModal: false,
  accessToken: undefined,
  direction: "ltr",
  userWhatsappDirection: "ltr",
  whatsappDarkMode: false, // Initialize from body class if available
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserAndCredentials: (
      state,
      action: PayloadAction<{ user?: UserInterface; accessToken?: string }>
    ) => {
      state.userId = action.payload.user?._id;
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user;
      console.log('[login-flow] UserReducer: setUserAndCredentials', {
        userId: state.userId,
        accessTokenSet: !!state.accessToken,
      });
    },
    setUser: (state, action: PayloadAction<UserInterface>) => {
      state.user = action.payload;
    },
    logout: (state) => {
      delete state.userId;
      delete state.accessToken;
      delete state.user;
      // Settled logged-out state (NOT 'idle', which the popup treats as loading —
      // that left the popup spinning after logout instead of showing the login form).
      state.authenticationStatus = 'autoJwtFail';
      // An explicit logout threw the credential away — there is nothing to retry.
      state.authFailureKind = 'no-credential';
    },
    clearAuthError: (state) => {
      delete state.authError;
    },
    setAuthenticationStatus: (state, action: PayloadAction<AuthenticationStatus>) => {
      state.authenticationStatus = action.payload;
      // A status written without a reason carries no classification. Clearing keeps a stale
      // kind (e.g. an old 'unreachable') from being read against a NEW failure — an
      // unclassified fail then falls back to the historical bounce-to-login behaviour.
      delete state.authFailureKind;
    },
    /**
     * Settle a FAILED auth together with its reason, in ONE action.
     *
     * Deliberately not two dispatches: a `setAuthenticationStatus('autoJwtFail')` followed by
     * a separate kind dispatch leaves an intermediate state of "failed, reason unknown", and a
     * render in that window would bounce an unreachable-backend session to /login — the exact
     * bug this fixes.
     */
    setAuthFailure: (state, action: PayloadAction<AuthFailureKind>) => {
      state.authenticationStatus = 'autoJwtFail';
      state.authFailureKind = action.payload;
    },
    setSignupState: (state, action: PayloadAction<SignupFormState>) => {
      state.signup = action.payload;
    },
    setHideSocketModal: (state, action: PayloadAction<boolean>) => {
      state.hideSocketModal = action.payload;
    },
    setUserDirection(state, { payload }: PayloadAction<string>) {
      state.direction = payload;
    },
    setUserWhatsappDirection(state, { payload }: PayloadAction<string>) {
      state.userWhatsappDirection = payload;
    },
    setAuthError: (state, action: PayloadAction<string>) => {
      state.authError = action.payload;
    },
    setWhatsappDarkMode: (state, action: PayloadAction<boolean>) => {
      state.whatsappDarkMode = action.payload;
    },
  }
});

export const {
  setUser,
  setUserAndCredentials,
  clearAuthError,
  logout,
  setAuthenticationStatus,
  setAuthFailure,
  setSignupState,
  setHideSocketModal,
  setUserDirection,
  setUserWhatsappDirection,
  setAuthError,
  setWhatsappDarkMode,
} = userSlice.actions;

export default userSlice.reducer;
